import { useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Wand2, Download, ArrowRight, Trash2, ShieldCheck, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { proposeReorder } from "@/lib/api";
import { cn } from "@/lib/utils";

export const ReorderDialog = ({ open, onOpenChange, rules }) => {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  // Reset when opening
  const handleOpenChange = (v) => {
    if (v) setResult(null);
    onOpenChange(v);
  };

  const compute = async () => {
    setLoading(true);
    try {
      const res = await proposeReorder(
        rules.map((r) => ({
          rule_id: r.rule_id,
          name: r.name,
          action: r.action,
          source: r.source,
          destination: r.destination,
          services: r.services || "",
          protocol: r.protocol || "",
          port: r.port || "",
        }))
      );
      setResult(res);
      toast.success(`Proposed: keep ${res.kept_count}, drop ${res.dropped_count}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reorder failed");
    } finally {
      setLoading(false);
    }
  };

  const download = () => {
    const blob = new Blob([result.csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `cleaned-ruleset-${Date.now()}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const droppedByStatus = useMemo(() => {
    if (!result) return { shadowed: 0, redundant: 0 };
    const shadowed = result.dropped.filter((r) => r.status === "shadowed").length;
    const redundant = result.dropped.filter((r) => r.status === "redundant").length;
    return { shadowed, redundant };
  }, [result]);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        data-testid="reorder-dialog"
        className="bg-[#0f172a] border-[#1e293b] rounded-sm max-w-3xl"
      >
        <DialogHeader>
          <div className="text-[10px] tracking-[0.25em] uppercase font-mono text-blue-400 mb-1">
            safe_cleanup
          </div>
          <DialogTitle className="text-slate-50 text-xl">
            Proposed ruleset cleanup
          </DialogTitle>
          <DialogDescription className="text-sm text-slate-400 mt-2 leading-relaxed">
            Drops <span className="text-red-400 font-mono">shadowed</span> and{" "}
            <span className="text-amber-400 font-mono">redundant</span> rules. Both are
            provably safe: shadowed rules never fire, and redundant rules produce the
            same action as an earlier rule. Effective policy is preserved.
          </DialogDescription>
        </DialogHeader>

        {!result ? (
          <div className="py-6">
            <Button
              data-testid="reorder-compute-btn"
              onClick={compute}
              disabled={loading}
              className="rounded-sm bg-blue-500 hover:bg-blue-400 text-white"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Wand2 className="h-4 w-4 mr-2" />
              )}
              Compute cleanup
            </Button>
          </div>
        ) : (
          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            <div className="grid grid-cols-3 gap-3">
              <div className="border border-emerald-500/30 rounded-sm p-4 bg-[#020617]">
                <div className="text-[10px] tracking-[0.2em] uppercase font-mono text-emerald-400 flex items-center gap-2 mb-2">
                  <ShieldCheck className="h-3 w-3" /> Kept
                </div>
                <div className="font-mono text-2xl text-emerald-400 font-semibold">
                  {result.kept_count}
                </div>
              </div>
              <div className="border border-red-500/30 rounded-sm p-4 bg-[#020617]">
                <div className="text-[10px] tracking-[0.2em] uppercase font-mono text-red-400 flex items-center gap-2 mb-2">
                  <Trash2 className="h-3 w-3" /> Shadowed
                </div>
                <div className="font-mono text-2xl text-red-400 font-semibold">
                  {droppedByStatus.shadowed}
                </div>
              </div>
              <div className="border border-amber-500/30 rounded-sm p-4 bg-[#020617]">
                <div className="text-[10px] tracking-[0.2em] uppercase font-mono text-amber-400 flex items-center gap-2 mb-2">
                  <Trash2 className="h-3 w-3" /> Redundant
                </div>
                <div className="font-mono text-2xl text-amber-400 font-semibold">
                  {droppedByStatus.redundant}
                </div>
              </div>
            </div>

            {result.dropped.length > 0 && (
              <div className="border border-[#1e293b] rounded-sm p-4 bg-[#020617]">
                <div className="text-[10px] tracking-[0.25em] uppercase font-mono text-slate-400 mb-3">
                  Rules dropped
                </div>
                <ul className="space-y-2 text-xs font-mono">
                  {result.dropped.map((r) => (
                    <li key={r.rule_id} className="flex items-start gap-2">
                      <span
                        className={cn(
                          "px-1.5 py-0.5 rounded-sm border text-[10px] tracking-[0.15em]",
                          r.status === "shadowed"
                            ? "text-red-400 border-red-500/30"
                            : "text-amber-400 border-amber-500/30"
                        )}
                      >
                        {r.status.toUpperCase()}
                      </span>
                      <span className="text-slate-100">#{r.rule_id}</span>
                      <span className="text-slate-500">{r.name}</span>
                      <ArrowRight className="h-3 w-3 mt-0.5 text-slate-700 shrink-0" />
                      <span className="text-slate-400 flex-1">{r.reason}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {result && (
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="rounded-sm border-[#1e293b] bg-transparent hover:bg-[#1e293b] text-slate-100"
            >
              Close
            </Button>
            <Button
              data-testid="reorder-download-btn"
              onClick={download}
              className="rounded-sm bg-blue-500 hover:bg-blue-400 text-white"
            >
              <Download className="h-4 w-4 mr-2" />
              Download cleaned CSV
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};
